import { useEffect, useState } from "react";

export interface OverlayDropZoneProps {
  /** Off while offline or before the page is ready: no scrim, drops ignored. */
  enabled: boolean;
  onFiles: (files: File[]) => void;
}

function carriesFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  return types !== undefined && Array.from(types).includes("Files");
}

/**
 * Drop a KMZ anywhere on the viewer page (spec 2026-09-09). Listens on the
 * window so the target is the whole page, and counts enter/leave pairs
 * because every child element fires its own pair as the pointer crosses it.
 * Nothing else on the page reacts to file drops; Leaflet does not handle
 * them, so there is no competing handler to defer to.
 */
export function OverlayDropZone({ enabled, onFiles }: OverlayDropZoneProps): JSX.Element | null {
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setDepth(0);
      return;
    }
    const onEnter = (e: DragEvent): void => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      setDepth((d) => d + 1);
    };
    const onOver = (e: DragEvent): void => {
      if (!carriesFiles(e)) return;
      // Without this the browser navigates to the dropped file.
      e.preventDefault();
    };
    const onLeave = (e: DragEvent): void => {
      if (!carriesFiles(e)) return;
      setDepth((d) => Math.max(0, d - 1));
    };
    const onDrop = (e: DragEvent): void => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      setDepth(0);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onFiles(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [enabled, onFiles]);

  if (!enabled || depth === 0) return null;
  return (
    <div className="vmc-drop-scrim" aria-hidden="true">
      <div className="vmc-drop-scrim-card">Drop to add as an overlay</div>
    </div>
  );
}
